import type { ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';

export type TonMessage = 'erreur' | 'information' | 'succes';

const GLYPHE: Readonly<Record<TonMessage, string>> = {
  erreur: '✕',
  information: 'i',
  succes: '✓',
};

const VARIANTE = {
  erreur: 'erreur',
  information: 'information',
  succes: 'succes',
} as const;

const TEINTE_GLYPHE: Readonly<Record<TonMessage, string>> = {
  erreur: 'bg-destructive text-destructive-foreground',
  information: 'bg-primary text-primary-foreground',
  succes: 'bg-etat-valide text-white',
};

export interface ProprietesMessage {
  readonly ton: TonMessage;
  readonly titre: string;
  readonly children?: ReactNode;
}

/**
 * Encart de message.
 *
 * `role="alert"` sur l'erreur : un agent qui vient de cliquer doit être averti
 * sans avoir à chercher où le texte est apparu. Les autres tons prennent
 * `role="status"`, qui n'interrompt pas la lecture en cours.
 *
 * Le glyphe est un caractère typographique, pas une image : zéro octet réseau,
 * rendu identique sur Android d'entrée de gamme. Il est `aria-hidden` — le titre
 * qui le suit dit la même chose en toutes lettres, et c'est lui qui est annoncé.
 */
export function Message({ ton, titre, children }: ProprietesMessage) {
  return (
    <Alert
      variant={VARIANTE[ton]}
      role={ton === 'erreur' ? 'alert' : 'status'}
      className="grid-cols-[calc(var(--spacing)*5)_1fr] gap-x-3"
    >
      <span
        aria-hidden="true"
        className={`mt-0.5 inline-flex size-5 items-center justify-center rounded-full text-xs font-bold leading-none ${TEINTE_GLYPHE[ton]}`}
      >
        {GLYPHE[ton]}
      </span>
      <AlertTitle>{titre}</AlertTitle>
      {children !== undefined && <AlertDescription>{children}</AlertDescription>}
    </Alert>
  );
}
